import { Badge } from 'src/design/atoms/Badge';
import { Box } from 'src/design/atoms/Box';
import { Paper } from 'src/design/atoms/Paper';
import { Stack } from 'src/design/atoms/Stack';
import { Text } from 'src/design/atoms/Text';
import { Title } from 'src/design/atoms/Title';
import React from 'react';
import {
  IRateBreakdownInstructions,
  RATE_BREAKDOWN_SCENARIO,
} from 'src/app/timesheets/types';
import {
  ScaleDerivationNote,
  ScaleNotAppliedDerivationNote,
  ScaleOvertimeDerivationNote,
} from './DerivationNotes';
import { RateFormula } from './RateFormula';

export interface IEffectiveRateBreakdownProps {
  instructions: IRateBreakdownInstructions;
}

/**
 * Renders the pay rate breakdown using server-supplied instructions. All scenario
 * decisions, formula structure, badge text, and step ordering come from the backend
 * (RateBreakdownInstructionBuilder); this component is a pure presentational mapper.
 * @registry Renders a rate breakdown (badge, formula grid, derivation notes) from server-supplied RateBreakdownInstructions-shaped data.
 */
export const EffectiveRateBreakdown: React.FC<IEffectiveRateBreakdownProps> = ({
  instructions,
}) => {
  const {
    badge,
    formulaTitle,
    formulaSteps,
    scaleDerivation,
    scaleOvertimeDerivation,
    scaleNotAppliedDerivation,
    scenario,
  } = instructions;

  const derivationNode =
    scenario === RATE_BREAKDOWN_SCENARIO.ScaleApplied && scaleDerivation ? (
      <ScaleDerivationNote derivation={scaleDerivation} />
    ) : scenario === RATE_BREAKDOWN_SCENARIO.ScaleOvertime &&
      scaleOvertimeDerivation ? (
      <ScaleOvertimeDerivationNote derivation={scaleOvertimeDerivation} />
    ) : scenario === RATE_BREAKDOWN_SCENARIO.ScaleNotApplied &&
      scaleNotAppliedDerivation ? (
      <ScaleNotAppliedDerivationNote derivation={scaleNotAppliedDerivation} />
    ) : null;

  return (
    <Paper withBorder={false}>
      <Stack gap="lg">
        <Stack gap={4}>
          <Title order={4}>How this rate is calculated</Title>
          {badge && (
            <Badge
              color={badge.color}
              variant="light"
              size="sm"
              w="fit-content"
            >
              {badge.label}
            </Badge>
          )}
        </Stack>

        <Stack gap="md">
          {derivationNode ? (
            <Box>
              {derivationNode}
              <RateFormula title={formulaTitle} steps={formulaSteps} />
            </Box>
          ) : (
            <RateFormula title={formulaTitle} steps={formulaSteps} />
          )}
          <Text size="xs" c="dimmed">
            Have questions? Contact your supervisor for assistance.
          </Text>
        </Stack>
      </Stack>
    </Paper>
  );
};
